import React, { useState, useEffect, useCallback } from 'react';
import Portal from './Portal';
import { db, storage } from '../firebase-config';
import {
  collection,
  query,
  where,
  onSnapshot,
  updateDoc,
  doc,
  getDocs,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

function formatDocDate(uploadedAt) {
  if (!uploadedAt) return '';
  try {
    if (typeof uploadedAt === 'string') return new Date(uploadedAt).toLocaleDateString();
    if (uploadedAt?.toDate) return uploadedAt.toDate().toLocaleDateString();
  } catch {
    /* ignore */
  }
  return '';
}

/**
 * @param {object} props
 * @param {import('firebase/auth').User | null} props.user
 * @param {object} [props.userData]
 * @param {string} [props.ownerIdForQueries] — Firestore ownerId for units/tenants (primary owner when user is delegated staff)
 * @param {string | null} [props.initialDetailTenantId] — open this tenant's detail when set (e.g. from unit view)
 * @param {function} [props.onConsumedInitialDetailTenantId]
 */
function TenantListPage({
  user,
  ownerIdForQueries,
  initialDetailTenantId,
  onConsumedInitialDetailTenantId,
}) {
  const [tenants, setTenants] = useState([]);
  const [unitsByNumber, setUnitsByNumber] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('');
  const [sortBy, setSortBy] = useState('fullName');
  const [sortDir, setSortDir] = useState('asc');

  const [detailTenantId, setDetailTenantId] = useState(null);
  const [detailSnap, setDetailSnap] = useState(null);
  const [uploadingDocs, setUploadingDocs] = useState(false);
  const [docMessage, setDocMessage] = useState('');

  useEffect(() => {
    if (initialDetailTenantId) {
      setDetailTenantId(initialDetailTenantId);
      if (onConsumedInitialDetailTenantId) onConsumedInitialDetailTenantId();
    }
  }, [initialDetailTenantId, onConsumedInitialDetailTenantId]);

  const ownerId = ownerIdForQueries || user?.uid || '';

  useEffect(() => {
    if (!user?.uid) {
      setError('Not authenticated');
      setLoading(false);
      return;
    }
    if (!ownerId) {
      setError('Missing owner scope');
      setLoading(false);
      return;
    }

    const qU = query(collection(db, 'units'), where('ownerId', '==', ownerId));
    const unsubUnits = onSnapshot(
      qU,
      (snap) => {
        const map = {};
        snap.docs.forEach((d) => {
          const u = d.data();
          if (u.unitNumber) map[String(u.unitNumber)] = u;
        });
        setUnitsByNumber(map);
      },
      (err) => console.error('units listener:', err)
    );

    const qT = query(collection(db, 'TenantsData'), where('ownerId', '==', ownerId));
    const unsubTenants = onSnapshot(
      qT,
      async (snap) => {
        const base = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const withReq = await Promise.all(
          base.map(async (t) => {
            try {
              const rq = query(
                collection(db, 'maintenance_requests'),
                where('tenantId', '==', t.id),
                where('status', '==', 'open')
              );
              const rs = await getDocs(rq);
              return { ...t, openRequestsCount: rs.size };
            } catch {
              return { ...t, openRequestsCount: 0 };
            }
          })
        );
        setTenants(withReq);
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error('tenants listener:', err);
        setError(`Failed to load tenants: ${err.message}`);
        setLoading(false);
      }
    );

    return () => {
      unsubUnits();
      unsubTenants();
    };
  }, [user?.uid, ownerId]);

  useEffect(() => {
    if (!detailTenantId) {
      setDetailSnap(null);
      return;
    }
    const ref = doc(db, 'TenantsData', detailTenantId);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setDetailSnap(null);
          return;
        }
        setDetailSnap({ id: snap.id, ...snap.data() });
      },
      (err) => console.error('tenant detail:', err)
    );
    return () => unsub();
  }, [detailTenantId]);

  const tenantsEnriched = tenants.map((t) => {
    const matched = unitsByNumber[String(t.unitNumber)] || {};
    return {
      ...t,
      monthlyRent: matched.monthlyRent ?? t.monthlyRent ?? 0,
      leaseExpiryDate: t.leaseExpiryDate ?? matched.leaseExpiryDate ?? null,
    };
  });

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return 'N/A';
    }
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);

  const filteredTenants = tenantsEnriched.filter((t) => {
    const q = filter.trim().toLowerCase();
    if (!q) return true;
    return (
      (t.fullName || '').toLowerCase().includes(q) ||
      (t.unitNumber || '').toString().toLowerCase().includes(q) ||
      (t.phoneNumber || '').toLowerCase().includes(q)
    );
  });

  function handleSort(col) {
    if (sortBy === col) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(col);
      setSortDir('asc');
    }
  }

  const sortedTenants = [...filteredTenants].sort((a, b) => {
    const av = a[sortBy] ?? '';
    const bv = b[sortBy] ?? '';
    if (typeof av === 'number' || typeof bv === 'number') {
      return sortDir === 'asc' ? av - bv : bv - av;
    }
    const as = String(av).toLowerCase();
    const bs = String(bv).toLowerCase();
    if (as < bs) return sortDir === 'asc' ? -1 : 1;
    if (as > bs) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const handleUploadTenantDoc = useCallback(
    async (e) => {
      const files = Array.from(e.target.files || []);
      if (!files.length || !detailTenantId || !user?.uid) return;
      setUploadingDocs(true);
      setDocMessage('');
      try {
        const current = Array.isArray(detailSnap?.documents) ? [...detailSnap.documents] : [];
        const newDocs = [];
        for (const file of files) {
          const path = `tenants/${detailTenantId}/documents/${Date.now()}_${file.name}`;
          const sref = ref(storage, path);
          await uploadBytes(sref, file);
          const url = await getDownloadURL(sref);
          newDocs.push({
            name: file.name,
            url,
            uploadedAt: new Date().toISOString(),
            size: file.size,
          });
        }
        await updateDoc(doc(db, 'TenantsData', detailTenantId), {
          documents: [...current, ...newDocs],
        });
        setDocMessage('✓ Document(s) uploaded');
        setTimeout(() => setDocMessage(''), 3000);
      } catch (err) {
        console.error(err);
        setDocMessage(`❌ ${err.message || 'Upload failed'}`);
      } finally {
        setUploadingDocs(false);
        e.target.value = '';
      }
    },
    [detailTenantId, detailSnap?.documents, user?.uid]
  );

  const handleDeleteTenantDoc = useCallback(
    async (index) => {
      if (!detailTenantId || !detailSnap) return;
      const docs = Array.isArray(detailSnap.documents) ? [...detailSnap.documents] : [];
      const row = docs[index];
      if (row?.source === 'rental_application') {
        if (!window.confirm('Remove the rental application PDF from this tenant’s file list?')) return;
      } else if (!window.confirm('Delete this document?')) {
        return;
      }
      setDocMessage('');
      try {
        docs.splice(index, 1);
        await updateDoc(doc(db, 'TenantsData', detailTenantId), { documents: docs });
        setDocMessage('✓ Removed');
        setTimeout(() => setDocMessage(''), 3000);
      } catch (err) {
        console.error(err);
        setDocMessage(`❌ ${err.message || 'Delete failed'}`);
      }
    },
    [detailTenantId, detailSnap]
  );

  if (loading) return <div className="tenants-loading">Loading tenants...</div>;

  return (
    <div className="tenants-container">
      <div className="tenants-header">
        <div>
          <h1>Active Tenants</h1>
          <p className="muted">{tenants.length} tenant{tenants.length !== 1 ? 's' : ''}</p>
        </div>

        <div className="tenants-controls">
          <input
            className="tenant-search"
            placeholder="Search tenants, unit, or phone..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>
      </div>

      {error && (
        <div
          style={{
            background: '#fee2e2',
            border: '1px solid #fecaca',
            color: '#dc2626',
            padding: '16px',
            borderRadius: '8px',
            marginBottom: '24px',
          }}
        >
          {error}
        </div>
      )}

      <div className="tenants-sort-bar">
        <span className="sort-label">Sort:</span>
        {[
          { key: 'fullName', label: 'Name' },
          { key: 'unitNumber', label: 'Unit' },
          { key: 'monthlyRent', label: 'Rent' },
          { key: 'leaseExpiryDate', label: 'Lease' },
          { key: 'openRequestsCount', label: 'Requests' },
        ].map(({ key, label }) => (
          <button
            key={key}
            type="button"
            className={`sort-btn${sortBy === key ? ' sort-btn-active' : ''}`}
            onClick={() => handleSort(key)}
          >
            {label}
            {sortBy === key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
          </button>
        ))}
      </div>

      {sortedTenants.length > 0 ? (
        <div className="tenants-grid">
          {sortedTenants.map((tenant) => (
            <button
              key={tenant.id}
              type="button"
              className="tenant-card"
              onClick={() => setDetailTenantId(tenant.id)}
              style={{
                cursor: 'pointer',
                textAlign: 'left',
                border: '1px solid #e2e8f0',
                background: '#fff',
              }}
            >
              <div className="tenant-card-header">
                <span className="tenant-avatar-lg">
                  {(tenant.fullName || 'T')
                    .split(' ')
                    .map((s) => s[0])
                    .slice(0, 2)
                    .join('')
                    .toUpperCase()}
                </span>
                <div className="tenant-card-identity">
                  <div className="tenant-fullname">{tenant.fullName || 'N/A'}</div>
                  <div className="tenant-sub">{tenant.email || '—'}</div>
                </div>
                {tenant.openRequestsCount > 0 && (
                  <span className="tenant-badge badge-open">{tenant.openRequestsCount} open</span>
                )}
              </div>
              <div className="tenant-card-body">
                <div className="tenant-stat">
                  <span className="tenant-stat-label">Unit</span>
                  <span className="tenant-stat-value">{tenant.unitNumber || 'N/A'}</span>
                </div>
                <div className="tenant-stat">
                  <span className="tenant-stat-label">Phone</span>
                  <span className="tenant-phone tenant-stat-value">{tenant.phoneNumber || 'N/A'}</span>
                </div>
                <div className="tenant-stat">
                  <span className="tenant-stat-label">Monthly Rent</span>
                  <span className="tenant-stat-value tenant-money">{formatCurrency(tenant.monthlyRent)}</span>
                </div>
                <div className="tenant-stat tenant-stat-last">
                  <span className="tenant-stat-label">Lease Expires</span>
                  <span className="tenant-stat-value">
                    {tenant.leaseExpiryDate ? formatDate(tenant.leaseExpiryDate) : 'N/A'}
                  </span>
                </div>
              </div>
              <div style={{ marginTop: 10, fontSize: 13, color: '#6366f1', fontWeight: 600 }}>
                View details & documents →
              </div>
            </button>
          ))}
        </div>
      ) : (
        <div className="empty-state">No active tenants yet.</div>
      )}

      {detailTenantId && (
        <Portal>
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="tenant-detail-title"
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 10040,
              background: 'rgba(15, 23, 42, 0.55)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 16,
            }}
            onClick={() => setDetailTenantId(null)}
          >
            <div
              style={{
                background: '#fff',
                borderRadius: 12,
                maxWidth: 520,
                width: '100%',
                maxHeight: '90vh',
                overflow: 'auto',
                boxShadow: '0 20px 50px rgba(0,0,0,0.2)',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ padding: '20px 22px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                <div>
                  <h2 id="tenant-detail-title" style={{ margin: 0, fontSize: 20, color: '#0f172a' }}>
                    {detailSnap?.fullName || 'Tenant'}
                  </h2>
                  <p style={{ margin: '6px 0 0', color: '#64748b', fontSize: 14 }}>{detailSnap?.email || '—'}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setDetailTenantId(null)}
                  style={{
                    border: 'none',
                    background: '#f1f5f9',
                    borderRadius: 8,
                    padding: '8px 12px',
                    cursor: 'pointer',
                    fontWeight: 600,
                  }}
                >
                  Close
                </button>
              </div>

              <div style={{ padding: '16px 22px' }}>
                <div style={{ display: 'grid', gap: 8, fontSize: 14, marginBottom: 20 }}>
                  <p style={{ margin: 0 }}>
                    <strong>Unit</strong> {detailSnap?.unitNumber || '—'}
                  </p>
                  <p style={{ margin: 0 }}>
                    <strong>Phone</strong>{' '}
                    {detailSnap?.phoneNumber ? (
                      <a href={`tel:${detailSnap.phoneNumber}`}>{detailSnap.phoneNumber}</a>
                    ) : (
                      '—'
                    )}
                  </p>
                </div>

                <h3 style={{ margin: '0 0 12px', fontSize: 16, color: '#0f172a' }}>Documents</h3>
                <p style={{ margin: '0 0 12px', color: '#64748b', fontSize: 13, lineHeight: 1.5 }}>
                  Lease, notices, and other files for this tenant. They see the same list in their portal (Documents tab).
                </p>

                {docMessage && (
                  <div
                    style={{
                      padding: '10px 12px',
                      borderRadius: 8,
                      marginBottom: 12,
                      background: docMessage.includes('✓') ? '#dcfce7' : '#fee2e2',
                      color: docMessage.includes('✓') ? '#166534' : '#991b1b',
                      fontSize: 13,
                    }}
                  >
                    {docMessage}
                  </div>
                )}

                {Array.isArray(detailSnap?.documents) && detailSnap.documents.length > 0 ? (
                  <ul style={{ margin: '0 0 16px', padding: 0, listStyle: 'none' }}>
                    {detailSnap.documents.map((d, i) => (
                      <li
                        key={`${d.url}-${i}`}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: 8,
                          padding: '10px 0',
                          borderBottom: '1px solid #f1f5f9',
                        }}
                      >
                        <div style={{ minWidth: 0 }}>
                          <a href={d.url} target="_blank" rel="noopener noreferrer" style={{ fontWeight: 600, color: '#2563eb', wordBreak: 'break-word' }}>
                            {d.name || 'Document'}
                          </a>
                          {d.source === 'rental_application' && (
                            <span style={{ fontSize: 11, fontWeight: 700, color: '#6366f1', marginLeft: 8 }}>Rental application</span>
                          )}
                          {formatDocDate(d.uploadedAt) ? (
                            <span style={{ fontSize: 12, color: '#94a3b8', marginLeft: 8 }}>{formatDocDate(d.uploadedAt)}</span>
                          ) : null}
                        </div>
                        <button
                          type="button"
                          onClick={() => handleDeleteTenantDoc(i)}
                          style={{
                            background: '#fee2e2',
                            color: '#b91c1c',
                            border: 'none',
                            borderRadius: 6,
                            padding: '4px 8px',
                            fontSize: 11,
                            cursor: 'pointer',
                            flexShrink: 0,
                          }}
                        >
                          Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p style={{ color: '#94a3b8', fontSize: 14, marginBottom: 16 }}>No documents yet.</p>
                )}

                <div
                  style={{
                    border: '2px dashed #cbd5e1',
                    borderRadius: 8,
                    padding: 16,
                    textAlign: 'center',
                    background: '#f8fafc',
                  }}
                >
                  <input
                    type="file"
                    multiple
                    id="tenant-doc-upload"
                    style={{ display: 'none' }}
                    disabled={uploadingDocs}
                    onChange={handleUploadTenantDoc}
                  />
                  <label htmlFor="tenant-doc-upload" style={{ cursor: uploadingDocs ? 'not-allowed' : 'pointer' }}>
                    <div style={{ fontSize: 22, marginBottom: 6 }}>📎</div>
                    <div style={{ fontWeight: 600, color: '#1f2937', fontSize: 14 }}>
                      {uploadingDocs ? 'Uploading…' : 'Upload files'}
                    </div>
                    <div style={{ color: '#64748b', fontSize: 12, marginTop: 4 }}>PDF, images, Word</div>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </Portal>
      )}
    </div>
  );
}

export default TenantListPage;
